import { ampli } from '@/ampli';
import { ButtonHTMLAttributes, forwardRef } from 'react';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';
import { LONG_TOKEN_SYMBOL_LENGTH } from '@/components/TransactionModule/utils';

import { buttons } from '@/styles/buttons';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ChevronDownSmallIcon } from '@/ui/Icons/ChevronDownSmallIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';
// utils
import { SEARCH_TYPES } from '@/utils/amplitude';
import { truncate } from '@/utils/string';
import { tokenProminentColorButtonStylesByThemeHSL } from '@/utils/tokenProminentColorButtonStyles';

type TokenPillProps = { activeToken?: TokenResult; isLoading?: boolean };

/** The token pill's classes, token-color variables, and content; a static pill has no caret or hover. */
function tokenPillParts({
  activeToken,
  isLoading = false,
  isStatic,
}: TokenPillProps & { isStatic: boolean }) {
  const buttonStyles = activeToken?.prominentColor
    ? tokenProminentColorButtonStylesByThemeHSL(activeToken.prominentColor)
    : { dark: undefined, light: undefined };

  const vars = {} as Record<string, string>;
  const txtVars = {} as Record<string, string>;
  let tokenWithLogoStyles = '';
  let textStyles = 'flex items-center justify-center';

  if (buttonStyles.light || buttonStyles.dark) {
    if (buttonStyles.light) {
      vars['--light-bg'] = String(buttonStyles.light.button.background);
      vars['--light-bg-hover'] = String(buttonStyles.light.buttonHover.background);
      vars['--dynamic-tokencolor-border-light'] = String(buttonStyles.light.button.borderColor);
      txtVars['--light-txt'] = String(buttonStyles.light.label.color);
    }
    if (buttonStyles.dark) {
      vars['--dark-bg'] = String(buttonStyles.dark.button.background);
      vars['--dynamic-tokencolor-border-dark'] = String(buttonStyles.dark.button.borderColor);
      vars['--dark-bg-hover'] = String(buttonStyles.dark.buttonHover.background);
      txtVars['--dark-txt'] = String(buttonStyles.dark.label.color);
    }

    tokenWithLogoStyles =
      'bg-[var(--light-bg)] hover:bg-[var(--light-bg-hover)] active:bg-[var(--light-bg-hover)] dark:bg-[var(--dark-bg)] dark:hover:bg-[var(--dark-bg-hover)] dark:active:bg-[var(--dark-bg-hover)]';
    textStyles +=
      ' data-[selected="false"]:text-[var(--light-txt)] dark:data-[selected="false"]:text-[var(--dark-txt)] data-[selected="true"]:text-[var(--light-txt)] dark:data-[selected="true"]:text-[var(--dark-txt)]';
  } else {
    tokenWithLogoStyles = 'bg-token-gray-200 hover:bg-token-gray-300';
    textStyles +=
      ' data-[selected="false"]:text-special-content-select-button-primary data-[selected="true"]:text-token-gray-1600';
  }
  const isLongTokenSymbol = activeToken && activeToken.symbol.length > LONG_TOKEN_SYMBOL_LENGTH;
  const className = buttons({
    size: 'small',
    color: activeToken || isLoading ? 'gray' : 'purple',
    shape: 'pill',
    class: [
      'w-fit',
      activeToken || isLoading ? 'pl-1.5' : 'pl-4',
      'pr-3',
      activeToken || isLoading
        ? 'shadow-[0_0_0_1px_var(--primitives-color-alphalight-300)_inset,0_-1px_0_0_var(--primitives-color-alphalight-300)_inset,0_0_0_1px_var(--dynamic-tokencolor-border-light,rgba(130,71,229,0.10))_inset,0_1px_3px_0_var(--primitives-color-alphalight-200)] dark:shadow-[0_0_0_1px_var(--primitives-color-alphadark-1200)_inset,0_1px_0_0_var(--primitives-color-alphadark-1100)_inset,0_0_0_1px_var(--dynamic-tokencolor-border-dark,rgba(130,71,229,0.20))_inset,0_1px_3px_0_var(--primitives-color-alphalight-200)]'
        : 'shadow-15',
      isLoading
        ? 'bg-token-gray-200'
        : activeToken
          ? activeToken.logo
            ? tokenWithLogoStyles
            : 'bg-[rgba(89,50,243,0.08)] active:bg-[rgba(89,50,243,0.24)] hover:bg-[rgba(89,50,243,0.16)]'
          : undefined,
      isStatic && 'pointer-events-none',
    ],
  });

  const children = (
    <>
      {isLoading ? (
        <AvatarSkeleton size={24} />
      ) : activeToken ? (
        <TokenAvatar
          address={activeToken.address}
          chainId={activeToken.chainId}
          size={24}
          showChainLogo={false}
        />
      ) : null}

      <span data-selected={!!activeToken || isLoading} className={textStyles} style={txtVars}>
        {isLoading ? (
          <span className="ml-2">
            <TextSkeleton width={48} height={14} />
          </span>
        ) : activeToken?.symbol ? (
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <span className="ml-2">
                {truncate(activeToken.symbol, LONG_TOKEN_SYMBOL_LENGTH, true)}
              </span>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>{activeToken.symbol}</MatchaTooltipContent>
            {isLongTokenSymbol && (
              <MatchaTooltipPopover sideOffset={0} style={{ maxWidth: 'fit-content' }} />
            )}
          </MatchaTooltip>
        ) : (
          <>
            <span>Select</span>
            <span className="hide-mobile">&nbsp;Token</span>
          </>
        )}

        {!isStatic && <ChevronDownSmallIcon className="ml-1.5" />}
      </span>
    </>
  );

  return { className, style: vars, children };
}

/** Renders the standard token-picker trigger without owning its dialog. */
export const SelectTokenTrigger = forwardRef<
  HTMLButtonElement,
  TokenPillProps & { searchType: SEARCH_TYPES } & ButtonHTMLAttributes<HTMLButtonElement>
>(function SelectTokenTrigger({ activeToken, searchType, isLoading, ...rest }, ref) {
  const { className, style, children } = tokenPillParts({
    activeToken,
    isLoading,
    isStatic: false,
  });
  return (
    <button
      ref={ref}
      {...rest}
      data-testid={`token-select-${searchType}`}
      data-chain-id={activeToken?.chainId}
      className={className}
      onClick={(event) => {
        rest.onClick?.(event);
        ampli.searchFocused({ search_type: searchType });
      }}
      style={style}
    >
      {children}
    </button>
  );
});

/** Shows a token as a fixed pill, styled like the picker trigger, for a side that can't change. */
export const TokenPill = (props: TokenPillProps) => {
  const { className, style, children } = tokenPillParts({ ...props, isStatic: true });
  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
};
